// RunCard — rendered inside a Column. Carries the run's identity, badges,
// progress, last event, and a hover-revealed cancel button.
//
// Visual fidelity: this is the prototype's `card.jsx` ported to TS.

import type { DragEvent, MouseEvent } from 'react';
import type { LastEvent, RunVM, Zone } from '../lib/types';

interface RunCardProps {
  run: RunVM;
  selected: boolean;
  isDragging: boolean;
  liveEvent: LastEvent | null;
  onOpen: (runId: string) => void;
  onCancel: (runId: string) => void;
  onPromote?: (runId: string) => void;
  onDragStart: (e: DragEvent<HTMLDivElement>, run: RunVM) => void;
  onDragEnd: () => void;
}

const ZONE_STYLES: Record<Zone, { bg: string; fg: string; label: string }> = {
  red: { bg: 'rgba(220,80,80,0.12)', fg: 'var(--red)', label: 'red' },
  green: { bg: 'rgba(80,200,140,0.10)', fg: 'var(--green)', label: 'green' },
  mixed: { bg: 'rgba(220,170,80,0.12)', fg: 'var(--amber)', label: 'mixed' },
};

function ZonePill({ zone }: { zone: Zone | null }) {
  if (!zone) return null;
  const s = ZONE_STYLES[zone];
  return (
    <span className="zone-pill" style={{ background: s.bg, color: s.fg }}>
      <span className="zone-dot" style={{ background: s.fg }} />
      {s.label}
    </span>
  );
}

function ModeBadge({ mode, local }: { mode: RunVM['mode']; local: boolean }) {
  return (
    <span className={`mode-badge mode-${mode}`}>
      {mode}
      {local && (
        <svg className="disk-icon" width="9" height="9" viewBox="0 0 9 9" fill="none">
          <rect x="0.5" y="0.5" width="8" height="8" rx="0.5" stroke="currentColor" />
          <rect x="2" y="0.5" width="5" height="3" fill="currentColor" opacity="0.4" />
          <rect x="3" y="5" width="3" height="3" stroke="currentColor" fill="none" />
        </svg>
      )}
    </span>
  );
}

function ProviderChip({ agent, reviewer }: { agent: string; reviewer: string }) {
  return (
    <span className="provider-chip">
      <span>{agent}</span>
      <span className="provider-arrow">→</span>
      <span>{reviewer}</span>
    </span>
  );
}

function PhaseBar({ phase, progress, loop }: {
  phase: RunVM['phase'];
  progress: number | null;
  loop: RunVM['loop'];
}) {
  const pct = Math.round(((progress ?? 0)) * 100);
  const label =
    phase === 'implementing' ? 'implementing' :
    phase === 'review' ? `review loop ${loop.current}/${loop.max}` :
    phase ?? '';
  return (
    <div className="phase-bar">
      <div className="phase-track">
        <div className="phase-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="phase-label">{label}</div>
    </div>
  );
}

function LastEventStrip({ event }: { event: LastEvent | null }) {
  if (!event) return null;
  return (
    <div className="last-event">
      <span className={`event-type event-${event.type}`}>{event.type}</span>
      <span className="event-text">{event.text}</span>
    </div>
  );
}

// `cost_usd` is stored as NUMERIC(15,9) precisely to preserve Haiku-scale
// values (the column was widened from NUMERIC(10,4) for this). `toFixed(2)`
// rounds anything below half a cent to "0.00", so every short / cheap run
// rendered as "$0.00" — visually identical to runs that genuinely cost
// nothing. Render extra decimals only when the value is below a penny so
// the common "$1.23" case keeps its familiar shape.
function formatCost(cost: number): string {
  if (!Number.isFinite(cost) || cost === 0) return '$0.00';
  if (cost < 0.01) return `$${cost.toFixed(4)}`;
  return `$${cost.toFixed(2)}`;
}

export function RunCard(props: RunCardProps) {
  const { run, selected, isDragging, liveEvent } = props;
  const event = liveEvent ?? run.lastEvent;
  const isDraft = run.status === 'draft';
  // 'implementing' / 'review' are in-flight; the same hover-revealed button
  // is also useful on parked-in-pr cards so the operator can clear a stale
  // / wrong / abandoned PR from the human queue without opening the drawer.
  // The backend's POST /runs/:id/cancel handles both: in-flight runs get
  // signaled to abort; parked runs transition directly to 'cancelled'.
  const showCancel = run.column === 'implementing'
    || run.column === 'review'
    || run.column === 'pr';
  const showPhase = run.column === 'implementing' || run.column === 'review';
  // Label adapts to mental model: "cancel" reads right for in-flight work,
  // "dismiss" reads right for a parked PR you just want off the board.
  const cancelLabel = run.column === 'pr' ? 'dismiss' : 'cancel';
  const cancelTitle = run.column === 'pr'
    ? 'Remove this PR from the human queue (PR stays on GitHub — you can merge or close it manually).'
    : 'Cancel the in-flight run.';

  const handleClick = (): void => props.onOpen(run.runId);
  const handleCancel = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    props.onCancel(run.runId);
  };
  const handlePromote = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    props.onPromote?.(run.runId);
  };
  const handleDiscard = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    props.onCancel(run.runId);
  };

  return (
    <div
      className={[
        'run-card',
        selected ? 'selected' : '',
        run.blocked ? 'blocked' : '',
        isDragging ? 'dragging' : '',
      ].filter(Boolean).join(' ')}
      draggable={run.column !== 'done'}
      onDragStart={(e) => props.onDragStart(e, run)}
      onDragEnd={props.onDragEnd}
      onClick={handleClick}
    >
      {run.blocked === 'reviewer-eq-agent' && (
        <div className="blocked-ribbon">
          reviewer = agent · pick a different reviewer
        </div>
      )}
      {run.warning === 'missing-ac' && (
        <div className="warn-ribbon">missing acceptance criteria</div>
      )}

      <div className="card-row card-row-top">
        <span className="ticket-id">[{run.id}]</span>
        <div className="card-row-right">
          {isDraft && <span className="src-tag draft-tag">draft</span>}
          {run.source === 'jira' && <span className="src-tag">jira</span>}
          {run.source === 'chat' && <span className="src-tag">chat</span>}
          <span className="cost">{formatCost(run.cost)}</span>
        </div>
      </div>

      <div className="card-title" title={run.title}>{run.title}</div>

      <div className="card-meta">
        <ModeBadge mode={run.mode} local={run.local} />
        <ProviderChip agent={run.agent} reviewer={run.reviewer} />
        <ZonePill zone={run.zone} />
      </div>

      {run.sandboxRegion && (
        <div className="sandbox-chip">cloud sandbox · {run.sandboxRegion}</div>
      )}

      {showPhase && <PhaseBar phase={run.phase} progress={run.progress} loop={run.loop} />}

      {run.column === 'review' && run.loop.issues > 0 && (
        <div className="review-meta">
          {run.loop.critical && <span className="critical-dot" />}
          {run.loop.issues} issue{run.loop.issues === 1 ? '' : 's'} found
          {run.loop.critical && <span className="critical-text"> · 1 critical</span>}
        </div>
      )}

      {run.column === 'pr' && run.pr && (
        <div className="pr-row">
          <span className="pr-num">#{run.pr.number}</span>
          <span className="diff-stat">
            <span className="diff-add">+{run.pr.additions}</span>
            <span className="diff-del">−{run.pr.deletions}</span>
          </span>
          <span className={`pr-label pr-${run.pr.label}`}>{run.pr.label}</span>
        </div>
      )}

      {run.column === 'done' && run.doneState && (
        <div className={`done-row done-${run.doneState}`}>
          <span className="done-dot" />
          {run.doneState}
        </div>
      )}

      <LastEventStrip event={event} />

      {isDraft && (
        <div className="draft-actions-row">
          <button className="draft-btn-sec" onClick={handleDiscard}>discard</button>
          <button className="draft-btn-pri" onClick={handlePromote}>queue run</button>
        </div>
      )}

      {showCancel && (
        <button
          // pr-column dismiss is a primary action on a parked run; it has
          // to be discoverable without hover (no touch-device hover, easy
          // to miss on desktop). The hover-reveal default stays for the
          // in-flight cancel button — that's a rare, destructive action
          // where requiring a hover gesture is intentional friction.
          className={run.column === 'pr' ? 'cancel-btn always-visible' : 'cancel-btn'}
          onClick={handleCancel}
          title={cancelTitle}
        >
          {cancelLabel}
        </button>
      )}
    </div>
  );
}
